import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Car, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const n = (v) => Number(v || 0).toLocaleString("es-DO");

const Card = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid={testid}>
    <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-2xl font-black font-display mt-1 leading-tight">{value}</div>
    {sub && <div className="text-[12px] font-semibold text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const RankList = ({ title, items, total, testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid={testid}>
    <div className="text-[13px] font-extrabold mb-3">{title}</div>
    <div className="space-y-2.5">
      {items.map((x) => (
        <div key={x.name}>
          <div className="flex justify-between text-[13px] font-bold gap-3">
            <span className="truncate">{x.name}</span>
            <span className="whitespace-nowrap">{n(x.n)}{x.pct != null ? <span className="text-gray-400 font-semibold"> · {x.pct}%</span> : null}</span>
          </div>
          <div className="h-1.5 rounded-full bg-emerald-50 mt-1 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-600" style={{ width: `${Math.round((x.n / Math.max(1, total)) * 100)}%` }}/>
          </div>
        </div>
      ))}
      {!items.length && <div className="text-[12px] text-gray-400 font-semibold">Sin datos</div>}
    </div>
  </div>
);

function Branches({ rows }) {
  const data = rows.filter((r) => r.si + r.no > 0);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 lg:col-span-2" data-testid="tip-branches">
      <div className="text-[13px] font-extrabold">Intención de tasación por sucursal</div>
      <div className="text-[11px] text-gray-500 font-semibold mb-2">Cantidad de oportunidades con y sin intención · el % de intención se ve al pasar el cursor</div>
      <div style={{ height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 20, right: 8, left: -14, bottom: 0 }}>
            <CartesianGrid stroke="#f1f5f9" vertical={false}/>
            <XAxis dataKey="sucursal" tick={{ fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} interval={0}/>
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false}/>
            <Tooltip formatter={(v, k) => [n(v), k]} labelFormatter={(l) => {
              const r = data.find((x) => x.sucursal === l); return `${l} · ${r?.pct_si ?? 0}% con intención`;
            }}/>
            <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }}/>
            <Bar dataKey="si" name="Con intención" fill="#059669" radius={[4, 4, 0, 0]}>
              <LabelList dataKey="si" position="top" formatter={(v) => n(v)} style={{ fontSize: 11, fontWeight: 800, fill: "#065f46" }}/>
            </Bar>
            <Bar dataKey="no" name="Sin intención" fill="#cbd5e1" radius={[4, 4, 0, 0]}>
              <LabelList dataKey="no" position="top" formatter={(v) => n(v)} style={{ fontSize: 11, fontWeight: 700, fill: "#64748b" }}/>
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Detail({ d }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="tip-detail">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center px-4 py-3 hover:bg-gray-50 transition-colors" data-testid="tip-detail-toggle">
        <span className="text-[13px] font-extrabold">Detalle por oportunidad <span className="text-gray-400 font-semibold">({n(d.detail_total)} con vehículo actual{d.detail_total > d.detail.length ? ` · últimas ${d.detail.length}` : ""})</span></span>
        <span className="text-[12px] font-extrabold text-emerald-700 flex items-center gap-1">{open ? "Ocultar" : "Ver detalle"} {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</span>
      </button>
      {open && (
        <div className="overflow-x-auto max-h-[460px] border-t border-gray-100">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 text-left border-b">
                {["Fecha", "Sucursal", "Ejecutivo", "Cliente", "Interés", "Veh. actual", "Intención", "Razón no tasación"].map((h) => <th key={h} className="px-3 py-2 font-extrabold whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {d.detail.map((r) => (
                <tr key={r.id} className="border-b border-gray-100">
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.fecha}</td>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{r.sucursal}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.ejecutivo || "—"}</td>
                  <td className="px-3 py-1.5">{r.cliente || "—"}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.interes || "—"}</td>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{[r.veh_marca, r.veh_modelo].filter(Boolean).join(" ")}</td>
                  <td className={`px-3 py-1.5 font-extrabold ${/^s/i.test(r.intencion || "") ? "text-emerald-700" : "text-gray-500"}`}>{r.intencion || "—"}</td>
                  <td className="px-3 py-1.5">{r.razon ? `${r.razon}${r.razon_otro ? ` · ${r.razon_otro}` : ""}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TradeInPotentialSection({ filters }) {
  const [d, setD] = useState(null);
  const key = JSON.stringify(filters);
  useEffect(() => {
    api.get("/trade-in-potential", { params: JSON.parse(key) }).then((r) => setD(r.data)).catch(() => setD(false));
  }, [key]);
  if (!d) return null;
  const c = d.cards;
  const yesTotal = d.vehicles.brands.reduce((s, x) => s + x.n, 0);
  return (
    <section className="surface p-6 space-y-4" data-testid="trade-in-potential-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Car size={18} className="text-emerald-700"/>
            <h2 className="text-base md:text-lg font-extrabold font-display">Potencial de Trade-In · Seminuevos</h2>
          </div>
          <p className="text-xs text-gray-500 font-semibold mt-0.5">Vehículo actual e intención de tasación registrados en las oportunidades de las sucursales. Visible solo para usuarios con permiso.</p>
        </div>
        {d.demo && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-[12px] font-extrabold" data-testid="tip-demo-badge">
            <AlertTriangle size={13}/> DATOS DE EJEMPLO · el archivo de Oportunidades aún no trae las columnas de tasación
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Con vehículo actual" value={n(c.con_vehiculo)} sub={`${c.pct_con_vehiculo}% de ${n(c.total)} oportunidades`} testid="tip-card-veh"/>
        <Card label="Con intención de tasación" value={`${c.pct_intencion}%`} sub={`${n(c.con_intencion)} oportunidades`} testid="tip-card-yes"/>
        <Card label="Sin intención de tasación" value={`${c.pct_sin_intencion}%`} sub={`${n(c.sin_intencion)} oportunidades`} testid="tip-card-no"/>
        <Card label="Razón principal de no tasación" value={<span className="text-[15px]">{c.top_razon || "—"}</span>} sub={d.reasons[0] ? `${d.reasons[0].pct}% de los que no tasan` : ""} testid="tip-card-reason"/>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Branches rows={d.by_branch}/>
        <div className="space-y-3">
          <RankList title="Razones de no tasación" testid="tip-reasons" total={c.sin_intencion}
                    items={d.reasons.map((r) => ({ name: r.razon, n: r.n, pct: r.pct }))}/>
          {d.otros_detalle.length > 0 && (
            <div className="text-[12px] font-semibold text-gray-600 px-1" data-testid="tip-otros">
              <span className="font-extrabold text-gray-700">&quot;Otros&quot; más mencionados:</span> {d.otros_detalle.map((o) => `${o.texto} (${o.n})`).join(" · ")}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RankList title="Marcas que entrarían (con intención de tasación)" items={d.vehicles.brands} total={yesTotal} testid="tip-veh-brands"/>
        <RankList title="Modelos que entrarían (con intención de tasación)" items={d.vehicles.models} total={yesTotal} testid="tip-veh-models"/>
      </div>
      <Detail d={d}/>
    </section>
  );
}
